/**
 * Configuration — Form Inventory
 * Behavioural rules for Forms, Form Modes and Form Fields registered in Trades Poster — Form Inventory.
 */

import {
  LIST_PREVIEW_COUNT,
  OWNING_MODULE_OPTIONS,
  buildSampleFormInventory,
  formatFormInventoryDate,
  formatFormInventoryDateTime,
  getFieldKey,
} from './formInventoryConfig';

export { LIST_PREVIEW_COUNT, formatFormInventoryDate, formatFormInventoryDateTime };

export const FORM_INVENTORY_CONFIGURATION_ROUTE = '/admin/configuration/form-inventory';

export const TARGET_TYPE_OPTIONS = ['Entire Form', 'Specific Form Mode', 'Specific Form Field'];

export const CONDITION_MATCH_OPTIONS = ['Match All', 'Match Any'];

export const RULE_STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

export const EVALUATION_STATUS_VALUES = [
  'Evaluating',
  'Not Evaluating — Rule Inactive',
  'Suspended — Target Form Inactive',
  'Suspended — Target Field Inactive',
];

export const FORM_MODE_EFFECTS = [
  'Show',
  'Hide',
  'Enable',
  'Disable',
  'Allow Submission',
  'Block Submission',
];

export const FIELD_EFFECT_OPTIONS = [
  'Show',
  'Hide',
  'Enable',
  'Disable',
  'Required',
  'Optional',
  'Editable',
  'Read-only',
  'Set Default Value',
  'Restrict Selectable Values',
];

export const CONFLICTING_EFFECT_PAIRS = [
  ['Show', 'Hide'],
  ['Enable', 'Disable'],
  ['Required', 'Optional'],
  ['Editable', 'Read-only'],
  ['Allow Submission', 'Block Submission'],
];

export const CONDITION_SOURCE_OPTIONS = [
  'Current User Type',
  'Current Form Mode',
  'Form Field Value',
  'Account Type',
  'Account Status',
  'Verification Status',
  'Pricing Plan',
  'Subscription Status',
  'Trade Category',
  'Jurisdiction',
  'Record Status',
  'Case Status',
  'Decision Status',
  'Saved Configuration Result',
];

export const CONDITION_OPERATOR_OPTIONS = [
  'Equals',
  'Does Not Equal',
  'Is One Of',
  'Is Not One Of',
  'Is True',
  'Is False',
  'Has Value',
  'Has No Value',
];

export const OPERATORS_WITHOUT_VALUE = ['Is True', 'Is False', 'Has Value', 'Has No Value'];

export const SAVED_CONFIGURATION_RESULT_REFERENCES = [
  'Configuration — Pricing Plan → Contact Details Available',
  'Configuration — Pricing Plan → External Links Available',
  'Configuration — Validation → Current Customer Verified',
  'Configuration — Workflow → Current Customer Account Active',
  'Configuration — Appeals → Appeal Configuration Result',
  'Configuration — Compliance → Compliance Configuration Result',
  'Configuration — Publishing → Publishing Configuration Result',
];

export const CURRENT_USER_TYPE_VALUES = [
  'Visitor',
  'Customer',
  'Business',
  'Platform Owner',
  'Co-owner',
  'Team Member',
];

export const VALIDATION_MESSAGES = {
  ruleNameMissing: 'Enter the Rule Name.',
  duplicateRuleName: 'A Form configuration rule with this name already exists.',
  targetTypeMissing: 'Select the Target Type.',
  targetFormMissing: 'Select the Target Form.',
  targetModeMissing: 'Select the Target Mode.',
  targetFieldMissing: 'Select the Target Form Field.',
  fieldBelongsToAnotherForm: 'Select a field belonging to the selected Target Form.',
  fieldNotConfigurable: 'This Form Field does not allow configuration.',
  conditionMatchMissing: 'Select Match All or Match Any.',
  noConditions: 'Add at least one Condition.',
  finalConditionRemoved: 'At least one Condition must remain in the rule.',
  conditionSourceMissing: 'Select the Condition Source.',
  conditionReferenceMissing: 'Select the required Condition Reference.',
  conditionOperatorMissing: 'Select the Condition Operator.',
  conditionValueMissing: 'Enter or select the Condition Value.',
  unrelatedFormField: 'A Form Field Value condition can reference only the selected Target Form.',
  noEffects: 'Add at least one Effect.',
  finalEffectRemoved: 'At least one Effect must remain in the rule.',
  effectNotPermitted: 'The selected effect is not permitted for this target.',
  submissionEffectOnField: 'Allow Submission and Block Submission cannot target a Form Field.',
  effectValueMissing: 'Enter or select the required Effect Value.',
  defaultValueInvalid: 'Select a value allowed by the targeted Form Field.',
  restrictedValueInvalid: 'Select only values registered for the targeted Form Field.',
  permanentRequirementWeakened: 'A field with Base Requirement Required cannot become Optional.',
  conflictingEffectsInRule: 'Remove the conflicting Effects before confirming the rule.',
  overlappingRuleConflict: 'This rule conflicts with the displayed Active rule. Update the Conditions or Effects.',
  targetFormInactive: 'The rule remains Active, but evaluation is suspended while the Form is Inactive.',
  targetFieldInactive: 'The rule remains Active, but evaluation is suspended while the field is Inactive.',
};

export const MESSAGES = {
  emptyState: 'No Form Inventory rules have been added yet.',
  saveConfirmTitle: 'Confirm this Form Inventory rule?',
  saveConfirmLabel: 'Save & Confirm',
  activateConfirmTitle: 'Activate this Form Inventory rule?',
  activateConfirmLabel: 'Activate',
  deactivateConfirmTitle: 'Deactivate this Form Inventory rule?',
  deactivateConfirmLabel: 'Deactivate',
  discardConfirmTitle: 'Discard unsaved changes?',
  discardConfirmLabel: 'Discard Changes',
};

const padId = (n, width = 6) => String(n).padStart(width, '0');

export const formatConfigurationId = (n) => `FORMCFG-${padId(n)}`;

export const nextConfigurationId = (rules = []) => {
  let max = 0;
  rules.forEach((rule) => {
    const match = String(rule.id || '').match(/^FORMCFG-(\d+)$/i);
    if (match) max = Math.max(max, Number(match[1]));
  });
  return formatConfigurationId(max + 1);
};

export const createEmptyCondition = (overrides = {}) => ({
  id: `cond-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  source: '',
  reference: '',
  operator: '',
  value: '',
  values: [],
  ...overrides,
});

export const createEmptyEffect = (overrides = {}) => ({
  id: `eff-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  effectType: '',
  effectValue: '',
  effectValues: [],
  ...overrides,
});

export const createEmptyRule = (overrides = {}) => ({
  id: '',
  ruleName: '',
  owningModule: '',
  targetType: '',
  targetFormId: '',
  targetFormName: '',
  targetMode: '',
  targetFieldId: '',
  targetFieldLabel: '',
  targetFieldInternalKey: '',
  conditionMatch: 'Match All',
  conditions: [createEmptyCondition()],
  effects: [createEmptyEffect()],
  ruleStatus: 'Active',
  evaluationStatus: 'Evaluating',
  dateAdded: '',
  addedBy: '',
  lastUpdated: '',
  lastUpdatedBy: '',
  ...overrides,
});

export const getFormById = (forms, formId) =>
  (forms || []).find((form) => form.id === formId) || null;

export const getFieldById = (form, fieldId) =>
  (form?.fields || []).find((field) => field.id === fieldId) || null;

export const getActiveForms = (forms = []) =>
  // Form Inventory no longer uses Active/Inactive; all saved skeletons are selectable.
  (forms || []).filter(Boolean);

export const getConfigurableFieldsForForm = (form, { includeInactiveSaved = false, savedFieldId } = {}) => {
  const fields = form?.fields || [];
  return fields.filter((field) => {
    if (savedFieldId && field.id === savedFieldId) return true;
    // Eligibility is owned outside Form Inventory; all skeleton fields may be targeted.
    if (field.configurationEligibility && field.configurationEligibility !== 'Configurable') {
      return false;
    }
    if (field.status && !includeInactiveSaved && field.status !== 'Active') return false;
    return true;
  });
};

export const getPermittedEffectsForTarget = (targetType, form, field) => {
  if (targetType === 'Entire Form' || targetType === 'Specific Form Mode') {
    return [...FORM_MODE_EFFECTS];
  }
  if (targetType === 'Specific Form Field') {
    const permitted = field?.permittedConfigurationEffects?.length
      ? field.permittedConfigurationEffects
      : [...FIELD_EFFECT_OPTIONS];
    if (field?.baseRequirement === 'Required') {
      return permitted.filter((effect) => effect !== 'Optional');
    }
    return [...permitted];
  }
  return [];
};

export const effectRequiresValue = (effectType) =>
  effectType === 'Set Default Value' || effectType === 'Restrict Selectable Values';

export const conditionRequiresReference = (source) =>
  source === 'Form Field Value' || source === 'Saved Configuration Result' || source === 'Current Form Mode';

export const conditionRequiresValue = (operator) =>
  Boolean(operator) && !OPERATORS_WITHOUT_VALUE.includes(operator);

export const getConditionReferences = (source, form) => {
  if (source === 'Saved Configuration Result') return [...SAVED_CONFIGURATION_RESULT_REFERENCES];
  if (source === 'Current Form Mode') return [...(form?.modes || [])];
  if (source === 'Form Field Value') {
    return (form?.fields || []).map((field) => ({
      value: field.id,
      label: `${field.id} — ${field.fieldLabel}`,
    }));
  }
  return [];
};

export const getConditionValueOptions = (source, reference) => {
  if (source === 'Current User Type') return [...CURRENT_USER_TYPE_VALUES];
  if (source === 'Saved Configuration Result') {
    if (String(reference || '').includes('Appeal Configuration Result')) {
      return ['Submission Allowed', 'Submission Blocked'];
    }
    if (String(reference || '').includes('Compliance Configuration Result')) {
      return ['Renewal Available', 'Renewal Unavailable'];
    }
    if (String(reference || '').includes('Publishing Configuration Result')) {
      return ['Submission Allowed', 'Submission Blocked'];
    }
    return ['Yes', 'No'];
  }
  if (source === 'Verification Status') return ['Verified', 'Unverified', 'Pending'];
  if (source === 'Account Status') return ['Active', 'Inactive', 'Suspended'];
  if (source === 'Account Type') return ['Customer', 'Business'];
  if (source === 'Subscription Status') return ['Active', 'Trial', 'Cancelled', 'Past Due'];
  if (source === 'Pricing Plan') return ['Basic', 'Pro'];
  return [];
};

export const formatConditionDisplay = (condition) => {
  if (!condition) return '—';
  const source = condition.source || '';
  const reference = condition.reference || '';
  const operator = condition.operator || '';
  const multi = Array.isArray(condition.values) && condition.values.length > 0;
  const value = multi
    ? condition.values.join(', ')
    : condition.value != null && condition.value !== ''
      ? String(condition.value)
      : '';

  if (condition.displayLabel) return condition.displayLabel;

  let subject = reference || source;
  if (source === 'Saved Configuration Result' && reference) {
    const arrow = reference.includes('→') ? reference.split('→').pop().trim() : reference;
    subject = arrow;
  }
  if (source === 'Current User Type') subject = 'Current User Type';
  if (source === 'Form Field Value' && reference) subject = reference;

  if (!operator) return subject || '—';
  if (!conditionRequiresValue(operator)) return `${subject} ${operator}`;
  return `${subject} ${operator === 'Equals' ? '=' : operator} ${value || '—'}`.replace('Equals ', '');
};

export const formatConditionListLabel = (condition) => {
  if (condition?.displayLabel) return condition.displayLabel;
  const source = condition?.source || '';
  const reference = condition?.reference || '';
  const multi = Array.isArray(condition?.values) && condition.values.length > 0;
  const value = multi
    ? condition.values.join(', ')
    : condition?.value != null
      ? String(condition.value)
      : '';

  if (source === 'Current User Type') {
    return `Current User Type = ${value || '—'}`;
  }
  if (source === 'Saved Configuration Result' && reference) {
    const arrow = reference.includes('→') ? reference.split('→').pop().trim() : reference;
    return `${arrow} = ${value || '—'}`;
  }
  return formatConditionDisplay(condition);
};

/** List-cell condition labels — expands multi-value operators for View All preview. */
export const getConditionPreviewLabels = (conditions = []) => {
  const labels = [];
  (conditions || []).forEach((condition) => {
    const multiOps = condition.operator === 'Is One Of' || condition.operator === 'Is Not One Of';
    if (multiOps && Array.isArray(condition.values) && condition.values.length > 0) {
      const subject =
        condition.source === 'Current User Type'
          ? 'Current User Type'
          : condition.reference?.includes('→')
            ? condition.reference.split('→').pop().trim()
            : condition.reference || condition.source || 'Value';
      condition.values.forEach((value, index) => {
        labels.push(index === 0 ? `${subject} = ${value}` : String(value));
      });
      return;
    }
    labels.push(formatConditionListLabel(condition));
  });
  return labels;
};

export const formatEffectsDisplay = (effects = []) =>
  (effects || [])
    .map((effect) => {
      if (!effect?.effectType) return '';
      if (!effectRequiresValue(effect.effectType)) return effect.effectType;
      const multi = Array.isArray(effect.effectValues) && effect.effectValues.length > 0;
      const value = multi ? effect.effectValues.join(', ') : effect.effectValue || '';
      return value ? `${effect.effectType}: ${value}` : effect.effectType;
    })
    .filter(Boolean);

export const formatTargetDisplay = (rule) => {
  if (!rule?.targetFormId) return '—';
  if (rule.targetType === 'Specific Form Field') {
    return `${rule.targetFormId} · ${rule.targetFieldId} · ${rule.targetFieldLabel || 'Field'}`;
  }
  if (rule.targetType === 'Specific Form Mode') {
    return `${rule.targetFormId} · ${rule.targetFormName || 'Form'} · ${rule.targetMode || 'Mode'}`;
  }
  return `${rule.targetFormId} · ${rule.targetFormName || 'Form'}`;
};

export const computeEvaluationStatus = (rule, forms = []) => {
  if (rule?.ruleStatus === 'Inactive') return 'Not Evaluating — Rule Inactive';
  const form = getFormById(forms, rule?.targetFormId);
  if (!form) return 'Suspended — Target Form Inactive';
  if (form.status && form.status !== 'Active') return 'Suspended — Target Form Inactive';
  if (rule?.targetType === 'Specific Form Field') {
    const field = getFieldById(form, rule.targetFieldId);
    if (!field) return 'Suspended — Target Field Inactive';
    if (field.status && field.status !== 'Active') return 'Suspended — Target Field Inactive';
  }
  return 'Evaluating';
};

export const enrichRuleFromInventory = (rule, forms = []) => {
  const form = getFormById(forms, rule.targetFormId);
  const field = form ? getFieldById(form, rule.targetFieldId) : null;
  return {
    ...rule,
    owningModule: form?.owningModule || rule.owningModule || '',
    targetFormName: form?.formName || rule.targetFormName || '',
    targetFieldLabel: field?.fieldLabel || rule.targetFieldLabel || '',
    targetFieldInternalKey: getFieldKey(field) || rule.targetFieldInternalKey || '',
    evaluationStatus: computeEvaluationStatus(rule, forms),
  };
};

export const getTargetTypeFilterOptions = () => [
  { value: 'all', label: 'All' },
  ...TARGET_TYPE_OPTIONS.map((value) => ({ value, label: value })),
];

export const getOwningModuleFilterOptions = (rules = []) => {
  const fromRules = [...new Set(rules.map((r) => r.owningModule).filter(Boolean))];
  const values = fromRules.length ? fromRules.sort() : [...OWNING_MODULE_OPTIONS].sort();
  return [{ value: 'all', label: 'All' }, ...values.map((v) => ({ value: v, label: v }))];
};

export const getEffectFilterOptions = (rules = []) => {
  const values = [
    ...new Set(
      (rules || []).flatMap((rule) => (rule.effects || []).map((effect) => effect.effectType).filter(Boolean)),
    ),
  ].sort();
  const fallback = [...new Set([...FORM_MODE_EFFECTS, ...FIELD_EFFECT_OPTIONS])].sort();
  const list = values.length ? values : fallback;
  return [{ value: 'all', label: 'All' }, ...list.map((v) => ({ value: v, label: v }))];
};

export const matchesFormInventoryConfigurationSearch = (rule, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return (
    String(rule.id || '')
      .toLowerCase()
      .includes(q) ||
    String(rule.ruleName || '')
      .toLowerCase()
      .includes(q) ||
    String(rule.targetFormId || '')
      .toLowerCase()
      .includes(q) ||
    String(rule.targetFormName || '')
      .toLowerCase()
      .includes(q) ||
    String(rule.targetFieldId || '')
      .toLowerCase()
      .includes(q) ||
    String(rule.targetFieldLabel || '')
      .toLowerCase()
      .includes(q) ||
    String(rule.targetFieldInternalKey || '')
      .toLowerCase()
      .includes(q)
  );
};

export const matchesFormInventoryConfigurationFilters = (rule, filters = {}) => {
  const { targetType = 'all', owningModule = 'all', effect = 'all', status = 'all' } = filters;
  if (targetType !== 'all' && rule.targetType !== targetType) return false;
  if (owningModule !== 'all' && rule.owningModule !== owningModule) return false;
  if (status !== 'all' && rule.ruleStatus !== status) return false;
  if (effect !== 'all') {
    const types = (rule.effects || []).map((item) => item.effectType);
    if (!types.includes(effect)) return false;
  }
  return true;
};

export const findRuleById = (rules = [], id) =>
  (rules || []).find((rule) => rule.id === id) || null;

export const findDuplicateRuleName = (rules = [], ruleName, excludeId) => {
  const name = String(ruleName || '')
    .trim()
    .toLowerCase();
  if (!name) return null;
  return (
    (rules || []).find(
      (rule) =>
        rule.id !== excludeId &&
        String(rule.ruleName || '')
          .trim()
          .toLowerCase() === name,
    ) || null
  );
};

const effectsConflict = (a = [], b = []) => {
  const typesA = a.map((e) => e.effectType).filter(Boolean);
  const typesB = b.map((e) => e.effectType).filter(Boolean);
  for (const [left, right] of CONFLICTING_EFFECT_PAIRS) {
    const aHasLeft = typesA.includes(left);
    const aHasRight = typesA.includes(right);
    const bHasLeft = typesB.includes(left);
    const bHasRight = typesB.includes(right);
    if ((aHasLeft && bHasRight) || (aHasRight && bHasLeft)) return true;
  }
  const aDefaults = a.filter((e) => e.effectType === 'Set Default Value').map((e) => e.effectValue);
  const bDefaults = b.filter((e) => e.effectType === 'Set Default Value').map((e) => e.effectValue);
  if (aDefaults.length && bDefaults.length && aDefaults.some((v) => bDefaults.some((other) => other && other !== v))) {
    return true;
  }
  return false;
};

const sameTarget = (a, b) => {
  if (a.targetType !== b.targetType || a.targetFormId !== b.targetFormId) return false;
  if (a.targetType === 'Specific Form Mode') return a.targetMode === b.targetMode;
  if (a.targetType === 'Specific Form Field') return a.targetFieldId === b.targetFieldId;
  return true;
};

/** Overlapping Active rules on the same target with conflicting effects. */
export const findOverlappingConflicts = (candidate, rules = [], excludeId) => {
  if (!candidate?.targetFormId || !candidate?.targetType) return [];
  return (rules || []).filter((rule) => {
    if (rule.id === excludeId) return false;
    if (rule.ruleStatus !== 'Active') return false;
    if (!sameTarget(rule, candidate)) return false;
    return effectsConflict(candidate.effects || [], rule.effects || []);
  });
};

export const findInternalEffectConflicts = (effects = []) => {
  const types = (effects || []).map((e) => e.effectType).filter(Boolean);
  const conflicts = [];
  CONFLICTING_EFFECT_PAIRS.forEach(([left, right]) => {
    if (types.includes(left) && types.includes(right)) {
      conflicts.push(`${left} and ${right}`);
    }
  });
  return conflicts;
};

export const buildSampleFormInventoryConfigurationRules = (formsInput) => {
  const forms = formsInput || buildSampleFormInventory();
  const stamp = '2026-08-04T10:15:00+10:00';
  const findForm = (name) => forms.find((form) => form.formName === name);
  const findField = (form, label) =>
    (form?.fields || []).find((field) => field.fieldLabel === label);

  const businessProfile = findForm('Business Profile Form');
  const websiteField =
    findField(businessProfile, 'Website') || findField(businessProfile, 'Email Address');
  const descriptionField =
    findField(businessProfile, 'Description') || findField(businessProfile, 'About');
  const contactSupport = findForm('Support Ticket Submission Form');
  const licenceSubmission = findForm('Compliance Decision Form');
  const createPost = findForm('Post Form');
  const addTeamMember = findForm('Team Member Form');

  const rules = [
    createEmptyRule({
      id: 'FORMCFG-000001',
      ruleName: 'Business Profile Website Visibility',
      owningModule: 'Businesses',
      targetType: 'Specific Form Field',
      targetFormId: businessProfile?.id || '',
      targetFormName: businessProfile?.formName || 'Business Profile Form',
      targetFieldId: websiteField?.id || '',
      targetFieldLabel: websiteField?.fieldLabel || 'Email Address',
      targetFieldInternalKey: getFieldKey(websiteField) || 'email_address',
      conditionMatch: 'Match All',
      conditions: [
        createEmptyCondition({
          id: 'cond-1a',
          source: 'Saved Configuration Result',
          reference: 'Configuration — Pricing Plan → Website Available',
          operator: 'Equals',
          value: 'Yes',
          displayLabel: 'Website Available = Yes',
        }),
        createEmptyCondition({
          id: 'cond-1b',
          source: 'Saved Configuration Result',
          reference: 'Configuration — Validation → Current Customer Verified',
          operator: 'Equals',
          value: 'Yes',
          displayLabel: 'Current Customer Verified = Yes',
        }),
        createEmptyCondition({
          id: 'cond-1c',
          source: 'Saved Configuration Result',
          reference: 'Configuration — Workflow → Current Customer Account Active',
          operator: 'Equals',
          value: 'Yes',
          displayLabel: 'Current Customer Account Active = Yes',
        }),
      ],
      effects: [createEmptyEffect({ id: 'eff-1', effectType: 'Show' })],
      ruleStatus: 'Active',
      dateAdded: stamp,
      addedBy: 'Platform Owner',
      lastUpdated: stamp,
      lastUpdatedBy: 'Platform Owner',
    }),
    createEmptyRule({
      id: 'FORMCFG-000002',
      ruleName: 'Business Profile Description Visibility',
      owningModule: 'Businesses',
      targetType: 'Specific Form Field',
      targetFormId: businessProfile?.id || '',
      targetFormName: businessProfile?.formName || 'Business Profile Form',
      targetFieldId: descriptionField?.id || '',
      targetFieldLabel: descriptionField?.fieldLabel || 'About',
      targetFieldInternalKey: getFieldKey(descriptionField) || 'about',
      conditionMatch: 'Match All',
      conditions: [
        createEmptyCondition({
          id: 'cond-2a',
          source: 'Saved Configuration Result',
          reference: 'Configuration — Pricing Plan → Description Available',
          operator: 'Equals',
          value: 'Yes',
          displayLabel: 'Description Available = Yes',
        }),
        createEmptyCondition({
          id: 'cond-2b',
          source: 'Saved Configuration Result',
          reference: 'Configuration — Validation → Current Customer Verified',
          operator: 'Equals',
          value: 'Yes',
          displayLabel: 'Current Customer Verified = Yes',
        }),
        createEmptyCondition({
          id: 'cond-2c',
          source: 'Saved Configuration Result',
          reference: 'Configuration — Workflow → Current Customer Account Active',
          operator: 'Equals',
          value: 'Yes',
          displayLabel: 'Current Customer Account Active = Yes',
        }),
      ],
      effects: [createEmptyEffect({ id: 'eff-2', effectType: 'Show' })],
      ruleStatus: 'Active',
      dateAdded: stamp,
      addedBy: 'Platform Owner',
      lastUpdated: stamp,
      lastUpdatedBy: 'Platform Owner',
    }),
    createEmptyRule({
      id: 'FORMCFG-000003',
      ruleName: 'Contact Support Availability',
      owningModule: 'Support Tickets',
      targetType: 'Entire Form',
      targetFormId: contactSupport?.id || '',
      targetFormName: contactSupport?.formName || 'Support Ticket Submission Form',
      conditionMatch: 'Match All',
      conditions: [
        createEmptyCondition({
          id: 'cond-3a',
          source: 'Saved Configuration Result',
          reference: 'Configuration — Support Tickets → Support Configuration Result',
          operator: 'Equals',
          value: 'Submission Allowed',
          displayLabel: 'Support Configuration Result = Submission Allowed',
        }),
      ],
      effects: [
        createEmptyEffect({ id: 'eff-3a', effectType: 'Show' }),
        createEmptyEffect({ id: 'eff-3b', effectType: 'Allow Submission' }),
      ],
      ruleStatus: 'Active',
      dateAdded: stamp,
      addedBy: 'Platform Owner',
      lastUpdated: stamp,
      lastUpdatedBy: 'Platform Owner',
    }),
    createEmptyRule({
      id: 'FORMCFG-000004',
      ruleName: 'Licence Submission Mode Availability',
      owningModule: 'Compliance',
      targetType: 'Specific Form Mode',
      targetFormId: licenceSubmission?.id || '',
      targetFormName: licenceSubmission?.formName || 'Compliance Decision Form',
      targetMode: licenceSubmission?.modes?.[0] || 'Decision',
      conditionMatch: 'Match All',
      conditions: [
        createEmptyCondition({
          id: 'cond-4a',
          source: 'Saved Configuration Result',
          reference: 'Configuration — Compliance → Compliance Configuration Result',
          operator: 'Equals',
          value: 'Submission Available',
          displayLabel: 'Compliance Configuration Result = Submission Available',
        }),
      ],
      effects: [
        createEmptyEffect({ id: 'eff-4a', effectType: 'Show' }),
        createEmptyEffect({ id: 'eff-4b', effectType: 'Enable' }),
      ],
      ruleStatus: 'Active',
      dateAdded: stamp,
      addedBy: 'Platform Owner',
      lastUpdated: stamp,
      lastUpdatedBy: 'Platform Owner',
    }),
    createEmptyRule({
      id: 'FORMCFG-000005',
      ruleName: 'Create Post Availability',
      owningModule: 'Posts',
      targetType: 'Specific Form Mode',
      targetFormId: createPost?.id || '',
      targetFormName: createPost?.formName || 'Post Form',
      targetMode: createPost?.modes?.[0] || 'Create',
      conditionMatch: 'Match All',
      conditions: [
        createEmptyCondition({
          id: 'cond-5a',
          source: 'Saved Configuration Result',
          reference: 'Configuration — Publishing → Publishing Configuration Result',
          operator: 'Equals',
          value: 'Submission Allowed',
          displayLabel: 'Publishing Configuration Result = Submission Allowed',
        }),
      ],
      effects: [createEmptyEffect({ id: 'eff-5', effectType: 'Allow Submission' })],
      ruleStatus: 'Active',
      dateAdded: stamp,
      addedBy: 'Platform Owner',
      lastUpdated: stamp,
      lastUpdatedBy: 'Platform Owner',
    }),
    createEmptyRule({
      id: 'FORMCFG-000006',
      ruleName: 'Add Team Member Availability',
      owningModule: 'Team Members',
      targetType: 'Entire Form',
      targetFormId: addTeamMember?.id || '',
      targetFormName: addTeamMember?.formName || 'Team Member Form',
      conditionMatch: 'Match Any',
      conditions: [
        createEmptyCondition({
          id: 'cond-6a',
          source: 'Current User Type',
          reference: '',
          operator: 'Is One Of',
          value: '',
          values: ['Platform Owner', 'Co-owner'],
        }),
      ],
      effects: [
        createEmptyEffect({ id: 'eff-6a', effectType: 'Show' }),
        createEmptyEffect({ id: 'eff-6b', effectType: 'Enable' }),
      ],
      ruleStatus: 'Active',
      dateAdded: stamp,
      addedBy: 'Platform Owner',
      lastUpdated: stamp,
      lastUpdatedBy: 'Platform Owner',
    }),
  ];

  return rules.map((rule) => enrichRuleFromInventory(rule, forms));
};
